import { LoadingSpinner } from '@/components/LoadingSpinner';
import { AiUnderwritingCtaAlert } from '@/shared/components/ai-underwriting-cta-alert';
import {
  AdjustedQuotesBanner,
  DEFAULT_FILTERS_STATE,
  GapFillBanner,
  QuoteErrorDisplay,
  ShopInsuranceAcknowledgmentOverlay,
  ShopInsuranceContext,
  ShopInsuranceFiltersSidebar,
  ShopInsuranceHeader,
  ShopInsuranceNotice,
  ShopInsuranceTabContent,
  ShopInsuranceTabsWrapper,
  getAssetCare20PayEligibility,
  getHideSinglePayAnd10Pay,
  getShowSinglePayAnd10Pay,
  clampPaymentOptionToEligibility,
  getShowShortTermCareOptions,
  getShowTraditionalLTCOptions,
  SHORT_TERM_CARE_PAYMENT_OPTION,
  ShortTermCareShopView,
  isClientMarriedOrPartnered,
  TRADITIONAL_LTC_PAYMENT_OPTIONS,
  useBenefitDesignInputs,
  useBenefitDesignDraftFilters,
  useBenefitDesignInputsStore,
  useCarrierExclusionsStore,
  useBenefitTypeFilterStore,
  useEligibilityFiltersStore,
  useQuoteFetching,
  useQuoteSelection,
  useShopInsuranceAcknowledgment,
  useShopInsuranceActiveFilters,
  useShopInsuranceModeQuotes,
  useShopInsuranceState,
  useShopInsuranceStoreReset,
  useShopInsuranceTraditionalLTC,
  useShopInsuranceUnderwriting,
  useSliderBounds,
  useSliderBoundsSync,
  calculateSliderBounds,
  getDefaultPremiumRange,
  normalizePremiumRange,
  getCachedFilterDefaults,
  toMinimumInflationPercent,
  toLegacyInflationRange,
  MAX_INFLATION_PERCENT,
  type CombinedFilterType,
  type FilterName,
  type PaymentOption,
  type PremiumFilterMode,
  type PremiumRange,
  type PremiumSort,
  type SliderBounds,
  type ShopInsuranceMode,
  type ShopInsuranceModalPropsOptions,
} from '@/features/shop-insurance';
import {
  AddPartnerDialog,
  LinkExistingPartnerDialog,
  PartnerRequiredDialog,
} from '@/features/add-partner';
import { PoliciesComparisonModal } from '@/features/policies-comparison';
import { AdvisorCustomDisclosures } from '@/features/client-onboarding/components/advisor-custom-disclosures';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@design-system';

import { Loader } from 'lucide-react';

import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { useDebouncedFormValue } from '@/hooks/useDebouncedFormValue';
import { useErrorToasts } from '@/hooks/useErrorToasts';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBeforeUnloadWarning } from '@/hooks/useBeforeUnloadWarning';
import { useShopInsuranceScrollAnchor } from '@/features/shop-insurance/hooks/use-scroll-anchor';
import {
  FormProvider,
  useForm,
  type UseFormGetValues,
  type UseFormSetValue,
} from 'react-hook-form';

import {
  selectActiveFundingSourceOperation,
  selectAllPaymentOptionQuotes,
  selectClient,
  selectClientCalculationSettings,
  selectFeatureFlags,
  selectHasFivePayQuotes,
  selectMaybeClientPartner,
  selectQuotes,
  selectQuotesError,
  selectQuotesIsLoading,
  selectTraditionalLTCQuotes,
  selectHasActiveIllustrationRequest,
  selectSession,
  selectSessionAdvisor,
} from '@/model';
import { isKnownLimitationError, isUnexpectedError } from '@/util/quoteTypeGuards';
import { SessionType, clientHasPartnerWithDemographics, isTobacco, zipCodeToState } from '@shared';

interface FormData {
  premium: number;
  premiumFilterMode: PremiumFilterMode;
  premiumRange: number[];
  premiumSort: PremiumSort;
  useCustomPremiumInput: boolean;
  useCustomPremiumRangeInput: boolean;
  benefitDuration: CombinedFilterType;
  /** Minimum inflation protection %; 0 means unfiltered. Used when the flag is on. */
  inflationProtection: number;
  /** Legacy AOB/COB range; used when `inflationProtectionMinimum` is off. */
  inflationProtectionRange: CombinedFilterType;
  inflationTypeFilter: 'all' | 'simple' | 'compound';
}

function usePremiumRangeSync({
  premiumRangeAvailable,
  premiumFilterMode,
  rangePremiumSliderBounds,
  paymentOption,
  getValues,
  setValue,
}: {
  premiumRangeAvailable: boolean;
  premiumFilterMode: PremiumFilterMode;
  rangePremiumSliderBounds: SliderBounds;
  paymentOption: PaymentOption;
  getValues: UseFormGetValues<FormData>;
  setValue: UseFormSetValue<FormData>;
}) {
  const previousPaymentOptionRef = useRef(paymentOption);
  const resetForPaymentOptionRef = useRef(false);

  useEffect(() => {
    if (previousPaymentOptionRef.current !== paymentOption) {
      previousPaymentOptionRef.current = paymentOption;
      resetForPaymentOptionRef.current = true;
    }

    if (!premiumRangeAvailable) {
      if (premiumFilterMode === 'range') setValue('premiumFilterMode', 'fixed');
      return;
    }
    if (premiumFilterMode !== 'range' || !rangePremiumSliderBounds.isCalculatedFromQuotes) return;

    const defaultRange = getDefaultPremiumRange(rangePremiumSliderBounds, paymentOption);
    const currentValue = getValues('premiumRange');
    const nextRange =
      resetForPaymentOptionRef.current || !Array.isArray(currentValue)
        ? defaultRange
        : normalizePremiumRange(
            { min: currentValue[0], max: currentValue[1] },
            rangePremiumSliderBounds,
          );

    if (
      !Array.isArray(currentValue) ||
      currentValue[0] !== nextRange.min ||
      currentValue[1] !== nextRange.max
    ) {
      setValue('premiumRange', [nextRange.min, nextRange.max]);
    }
    resetForPaymentOptionRef.current = false;
  }, [
    getValues,
    paymentOption,
    premiumFilterMode,
    premiumRangeAvailable,
    rangePremiumSliderBounds,
    setValue,
  ]);
}

export function ShopInsuranceContent({
  onClose,
  queryOptions,
}: {
  onClose: () => void;
  queryOptions?: ShopInsuranceModalPropsOptions;
}) {
  const navigate = useNavigate();
  const [showAddPartnerDialog, setShowAddPartnerDialog] = useState(false);
  const [showLinkExistingDialog, setShowLinkExistingDialog] = useState(false);
  const [showComparePolicies, setShowComparePolicies] = useState(false);
  const { sessionType } = useSelector(selectSession);
  const isAdvisor = sessionType === SessionType.advisor || sessionType === SessionType.wizard;
  const { flags } = useFeatureFlags();
  const skipAutoFetch = queryOptions?.skipAutoFetch ?? false;
  useErrorToasts({ open: true });

  // Illustration generation takes 1-2 minutes and does not survive a reload, so warn before the
  // page goes away. Native prompt only — the app has no in-app navigation blocker to reuse.
  const hasActiveIllustrationRequest = useSelector(selectHasActiveIllustrationRequest);
  useBeforeUnloadWarning(hasActiveIllustrationRequest);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  useShopInsuranceScrollAnchor(scrollContainerRef);

  const quotes = useSelector(selectQuotes);
  const traditionalLTCQuotes = useSelector(selectTraditionalLTCQuotes);
  const allPaymentOptionQuotes = useSelector(selectAllPaymentOptionQuotes);
  const client = useSelector(selectClient);
  const clientId = client?.clientId;
  const advisor = useSelector(selectSessionAdvisor);
  const { advisorId } = advisor;

  useShopInsuranceStoreReset(clientId);

  const savedFilterDefaults = useMemo(() => {
    const cached = getCachedFilterDefaults(advisorId);
    if (cached) return cached;
    return (advisor?.shopInsuranceFilterDefaults ?? null) as ShopInsuranceFilterDefaults | null;
  }, [advisorId, advisor?.shopInsuranceFilterDefaults]);
  const clientPartner = useSelector(selectMaybeClientPartner);
  const clientCalculationSettings = useSelector(selectClientCalculationSettings);
  const state = zipCodeToState(client.intakeSurvey.clientZipCode);

  const error = useSelector(selectQuotesError);
  const isLoading = useSelector(selectQuotesIsLoading);
  const hasFivePayQuotes = useSelector(selectHasFivePayQuotes);
  const featureFlags = useSelector(selectFeatureFlags);
  const isD2C = sessionType === SessionType.client && !!featureFlags.d2cMode;

  const isShortTermCareEligible = getShowShortTermCareOptions(
    featureFlags.quoteApplyManhattanLife,
    client.intakeSurvey.clientZipCode,
    client.intakeSurvey.clientBirthDate,
  );
  const showTraditionalLTCOptions = getShowTraditionalLTCOptions(
    client.intakeSurvey.clientZipCode,
    client.intakeSurvey.clientBirthDate,
  );
  const showSinglePayAnd10Pay = getShowSinglePayAnd10Pay(
    featureFlags.hideSinglePayAnd10Pay,
    featureFlags.quoteApplyNewYorkLife,
    client.intakeSurvey.clientBirthDate,
  );

  // Both tabs' answers, because 20-Pay availability is not a property of the
  // client alone: single life needs this client to be a scraped cohort, joint
  // life needs this client and the partner to be the scraped couple.
  const assetCare20PayEligibility = useMemo(
    () => getAssetCare20PayEligibility(client.intakeSurvey, clientPartner?.intakeSurvey),
    [client.intakeSurvey, clientPartner?.intakeSurvey],
  );

  // The tab useShopInsuranceState is about to start on, derived from the same
  // client/partner pair it derives it from. Needed here because a restored
  // 20-Pay has to be clamped against the tab that will actually be showing:
  // clamping against "eligible on either tab" would leave 20-Pay selected on a
  // Joint tab that cannot serve it, which is the blank-selector case the clamp
  // exists to prevent.
  const initialTab: ShopInsuranceTab =
    queryOptions?.selectTabOption ??
    (clientHasPartnerWithDemographics(client, clientPartner) ? 'joint' : 'single');
  const is20PayEligibleOnInitialTab =
    initialTab === 'joint'
      ? assetCare20PayEligibility.isJointEligible
      : assetCare20PayEligibility.isSingleEligible;

  const {
    selectedTab,
    setSelectedTab,
    paymentOption,
    mode,
    expandedIndices,
    setExpandedIndices,
    hasAcknowledged,
    showAcknowledgment,
    selectedBuyerOption,
    hasPartnerWithDemographics,
    hasSameGenderPartner,
    showPartnerRequiredDialog,
    setShowPartnerRequiredDialog,
    handleTabChange,
    handleCardToggle,
    handleAcknowledgment,
    setShowAcknowledgment,
    handleBuyerOptionChange,
    handlePaymentOptionChange,
    handleModeChange,
  } = useShopInsuranceState({
    hideSinglePayAnd10Pay: getHideSinglePayAnd10Pay(
      featureFlags.hideSinglePayAnd10Pay,
      featureFlags.quoteApplyNewYorkLife,
      client.intakeSurvey.clientBirthDate,
    ),
    providedBuyerOption: queryOptions?.buyerOption,
    // Saved advisor defaults and query params travel across clients, so a
    // restored 'Short-Term Care' can land on a client who is not STC-eligible:
    // the option would be absent from the dropdown while still being the
    // selected value — a blank selector, no quotes, and no way back.
    providedPaymentOption: clampPaymentOptionToEligibility(
      queryOptions?.paymentOption ??
        (savedFilterDefaults?.paymentOption as PaymentOption | undefined),
      isShortTermCareEligible,
      is20PayEligibleOnInitialTab,
    ),
    providedSelectedTab: queryOptions?.selectTabOption,
    providedMode:
      queryOptions?.mode ?? (savedFilterDefaults?.mode as ShopInsuranceMode | undefined),
  });

  const { fetchQuotesForPolicyType } = useQuoteFetching({
    open: true,
    selectedTab,
    paymentOption,
    hasPartnerWithDemographics,
    skipAutoFetch,
  });

  const premiumRangeEnabled = Boolean(flags.shopInsurancePremiumRange);
  const premiumRangeAvailable =
    premiumRangeEnabled &&
    !TRADITIONAL_LTC_PAYMENT_OPTIONS.includes(paymentOption) &&
    paymentOption !== SHORT_TERM_CARE_PAYMENT_OPTION;
  const fixedPremiumSliderBounds = useSliderBounds(quotes, selectedTab, paymentOption === '5-Pay');
  const rangePremiumSliderBounds = useMemo(() => {
    if (!premiumRangeEnabled || fixedPremiumSliderBounds.isCalculatedFromQuotes) {
      return fixedPremiumSliderBounds;
    }
    try {
      return calculateSliderBounds(quotes, selectedTab, true);
    } catch (error) {
      console.error('Error calculating premium range slider bounds:', error);
      return fixedPremiumSliderBounds;
    }
  }, [fixedPremiumSliderBounds, premiumRangeEnabled, quotes, selectedTab]);
  const { isQuoteSelected } = useQuoteSelection({ selectedTab });
  const initialPremiumRange = getDefaultPremiumRange(rangePremiumSliderBounds, paymentOption);

  const returnOnInvestmentCalculationType =
    clientCalculationSettings?.returnOnInvestmentCalculationType || 'total';

  const formMethods = useForm<FormData>({
    defaultValues: {
      benefitDuration:
        (savedFilterDefaults?.benefitDuration as CombinedFilterType) ??
        DEFAULT_FILTERS_STATE.benefitDuration,
      inflationProtection:
        toMinimumInflationPercent(savedFilterDefaults?.inflationProtection) ??
        DEFAULT_FILTERS_STATE.inflationProtection,
      inflationProtectionRange:
        (toLegacyInflationRange(
          savedFilterDefaults?.inflationProtection,
          MAX_INFLATION_PERCENT,
        ) as CombinedFilterType) ?? DEFAULT_FILTERS_STATE.inflationProtectionRange,
      inflationTypeFilter:
        (savedFilterDefaults?.inflationType as 'all' | 'simple' | 'compound') ??
        DEFAULT_FILTERS_STATE.inflationTypeFilter,
      useCustomPremiumInput: savedFilterDefaults?.useCustomPremiumInput ?? false,
      useCustomPremiumRangeInput: false,
      premium: fixedPremiumSliderBounds.defaultValue,
      premiumFilterMode: 'fixed',
      premiumRange: [initialPremiumRange.min, initialPremiumRange.max],
      premiumSort: 'modeled-roi',
    },
  });

  const { watch, reset, getValues, setValue } = formMethods;

  // Slider-driven fields subscribe without rendering, so dragging does not re-render
  // this tree on every step — only when the value settles.
  //
  // Premium is committed once per interaction (slider release, or a fully in-range entry
  // in the custom field) rather than once per step, so it needs only enough delay to
  // coalesce a burst of keystrokes — any more is latency the user feels as slow quotes.
  const premiumValue = useDebouncedFormValue<FormData, number>(formMethods, 'premium', 100);
  const premiumFilterMode = formMethods.watch('premiumFilterMode') ?? 'fixed';
  const premiumSort = formMethods.watch('premiumSort') ?? 'modeled-roi';
  const sliderBounds =
    premiumRangeAvailable && premiumFilterMode === 'range'
      ? rangePremiumSliderBounds
      : fixedPremiumSliderBounds;
  const premiumRangeValue = useDebouncedFormValue<FormData, number[]>(
    formMethods,
    'premiumRange',
    100,
  );
  const premiumRange = useMemo<PremiumRange>(
    () =>
      normalizePremiumRange(
        {
          min: premiumRangeValue?.[0] ?? sliderBounds.min,
          max: premiumRangeValue?.[1] ?? sliderBounds.max,
        },
        sliderBounds,
      ),
    [premiumRangeValue, sliderBounds],
  );
  const benefitDurationValue = useDebouncedFormValue<FormData, CombinedFilterType>(
    formMethods,
    'benefitDuration',
    200,
  );
  const inflationProtectionValue = useDebouncedFormValue<FormData, number>(
    formMethods,
    'inflationProtection',
    200,
  );
  const inflationProtectionRangeValue = useDebouncedFormValue<FormData, CombinedFilterType>(
    formMethods,
    'inflationProtectionRange',
    200,
  );
  const inflationTypeFilterValue = watch('inflationTypeFilter');
  const useMinimumInflation = flags.inflationProtectionMinimum ?? false;

  // Full cost the client faces = care + out-of-pocket room & board (IL only).
  // Room & board is never coverable, so IL coverage can't reach 100%.
  const totalCost = client.allPhaseCosts.allPhaseInflatedTotalCost;
  const [isGapFillMode, setIsGapFillMode] = useState(false);
  const setGapFillMode = useCallback((active: boolean) => setIsGapFillMode(active), []);
  const isCoverageGapEnabled = flags.coverageGap ?? false;

  useSliderBoundsSync({
    sliderBounds: fixedPremiumSliderBounds,
    paymentOption,
    reset,
    getValues,
    setValue,
    savedFilterDefaults,
  });

  usePremiumRangeSync({
    premiumRangeAvailable,
    premiumFilterMode,
    rangePremiumSliderBounds,
    paymentOption,
    getValues,
    setValue,
  });

  const storeDefaultsAppliedForClientRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (storeDefaultsAppliedForClientRef.current === clientId || !savedFilterDefaults) return;
    storeDefaultsAppliedForClientRef.current = clientId;
    const { actions: carrierActions } = useCarrierExclusionsStore.getState();
    const { actions: benefitTypeActions } = useBenefitTypeFilterStore.getState();
    const { actions: eligibilityActions } = useEligibilityFiltersStore.getState();

    if (savedFilterDefaults.hiddenCarriers) {
      carrierActions.setHiddenCarriers(savedFilterDefaults.hiddenCarriers);
    }
    if (savedFilterDefaults.benefitPaymentType) {
      benefitTypeActions.setSelectedBenefitPaymentType(
        savedFilterDefaults.benefitPaymentType as any,
      );
    }
    if (savedFilterDefaults.eligibilityFilters) {
      eligibilityActions.setSelectedEligibilityFilters(savedFilterDefaults.eligibilityFilters);
    }
    if (savedFilterDefaults.featureBenefitsFilters) {
      eligibilityActions.setSelectedFeatureBenefitsFilters(
        savedFilterDefaults.featureBenefitsFilters,
      );
    }
    if (savedFilterDefaults.benefitDesignInputs) {
      const { actions: bdActions } = useBenefitDesignInputsStore.getState();
      const bd = savedFilterDefaults.benefitDesignInputs;
      const monthlyBenefitTiming = bd.monthlyBenefitTiming ?? 'policy-issue';
      const legacyMonthlyBenefit =
        monthlyBenefitTiming === 'care-start'
          ? bd.monthlyBenefitAtCareStart
          : bd.monthlyBenefitAtPolicyIssue;
      bdActions.applyMonthlyBenefitSelection({
        monthlyBenefit: bd.monthlyBenefit ?? legacyMonthlyBenefit ?? null,
        timing: monthlyBenefitTiming,
      });
      if (bd.benefitDuration) bdActions.setBenefitDuration(bd.benefitDuration as any);
      // Hydrate both shapes so the saved default survives the flag being toggled either way.
      const savedMinimumInflation = toMinimumInflationPercent(bd.inflationProtection);
      if (savedMinimumInflation !== null) {
        bdActions.setInflationProtection(savedMinimumInflation);
      }
      const savedInflationRange = toLegacyInflationRange(
        bd.inflationProtection,
        MAX_INFLATION_PERCENT,
      );
      if (savedInflationRange) {
        bdActions.setInflationProtectionRange(savedInflationRange as CombinedFilterType);
      }
      if (bd.inflationTypes) bdActions.setInflationTypes(bd.inflationTypes as any);
      if (bd.benefitPaymentType) bdActions.setBenefitPaymentType(bd.benefitPaymentType as any);
    }
  }, [savedFilterDefaults, clientId]);

  const benefitDesignInputs = useBenefitDesignInputs();

  const availableCarriers = useMemo(() => {
    const carrierSet = new Set<string>();
    const carrierSource =
      mode === 'benefit-design' && allPaymentOptionQuotes.length ? allPaymentOptionQuotes : quotes;
    carrierSource?.forEach(q => {
      if (q.carrierName) carrierSet.add(q.carrierName);
    });
    return Array.from(carrierSet).sort();
  }, [mode, allPaymentOptionQuotes, quotes]);

  // Keep one By Benefit draft for the desktop sidebar, mobile sheet, and active-filter banner.
  // Removing a chip can then apply the exact values currently visible in either filter panel.
  const benefitDesignDraftFilters = useBenefitDesignDraftFilters(availableCarriers);

  const { filteredTraditionalLTCQuotes } = useShopInsuranceTraditionalLTC({
    paymentOption,
    selectedBuyerOption,
    hasPartnerWithDemographics,
    advisorId,
    returnOnInvestmentCalculationType,
  });

  const activePremiumRange =
    premiumRangeAvailable &&
    premiumFilterMode === 'range' &&
    sliderBounds.isCalculatedFromQuotes &&
    mode === 'premium-optimization' &&
    !isGapFillMode
      ? premiumRange
      : undefined;
  const effectivePremiumValue = activePremiumRange?.min ?? premiumValue;

  const {
    adjustedQuotes,
    activeFilters,
    benefitDesignQuoteGroups,
    benefitDesignAvailableOptions,
    modeAdjustedQuotes,
    modeTraditionalLTCQuotes,
  } = useShopInsuranceModeQuotes({
    mode,
    paymentOption,
    selectedTab,
    client,
    quotes,
    allPaymentOptionQuotes,
    premiumValue: effectivePremiumValue,
    premiumRange: activePremiumRange,
    premiumSort,
    benefitDurationValue,
    inflationProtectionValue,
    inflationProtectionRangeValue,
    inflationTypeFilterValue,
    benefitDesignInputs,
    returnOnInvestmentCalculationType,
    hasPartnerWithDemographics,
    filteredTraditionalLTCQuotes,
    isGapFillMode,
    totalCost,
  });

  useShopInsuranceAcknowledgment({
    open: true,
    isLoading,
    quotes,
    hasAcknowledged,
    setShowAcknowledgment,
  });

  const {
    eligibilityCounts,
    filteredAdjustedQuotes,
    filteredTraditionalLTCQuotesWithEligibility,
    showEligibilityFilter,
  } = useShopInsuranceUnderwriting({
    clientId: client?.clientId,
    selectedTab,
    adjustedQuotes: modeAdjustedQuotes,
    traditionalLTCQuotes: modeTraditionalLTCQuotes,
  });

  function is20PayEligibleOnTab(tab: ShopInsuranceTab) {
    return tab === 'joint'
      ? assetCare20PayEligibility.isJointEligible
      : assetCare20PayEligibility.isSingleEligible;
  }

  useEffect(() => {
    if (paymentOption !== '5-Pay' || hasFivePayQuotes !== false) return;

    const fallbackPaymentOption: PaymentOption | undefined = showSinglePayAnd10Pay
      ? '10-Pay'
      : showTraditionalLTCOptions
        ? 'Annually'
        : isShortTermCareEligible
          ? SHORT_TERM_CARE_PAYMENT_OPTION
          : undefined;

    if (fallbackPaymentOption) {
      handlePaymentOptionChange(fallbackPaymentOption);
    }
  }, [
    handlePaymentOptionChange,
    hasFivePayQuotes,
    isShortTermCareEligible,
    paymentOption,
    showSinglePayAnd10Pay,
    showTraditionalLTCOptions,
  ]);

  function handleTabChangeWithFetch(tabValue: string) {
    const tabChanged = handleTabChange(tabValue);
    if (!tabChanged) return;

    const tab = tabValue as ShopInsuranceTab;
    // A client can be a scraped single-life cohort without being half of the
    // scraped couple, so 20-Pay can be valid on one tab and empty on the other.
    // Leaving it selected across that switch shows an option the dropdown no
    // longer offers and fetches a combination with no rows.
    const nextPaymentOption =
      paymentOption === '20-Pay' && !is20PayEligibleOnTab(tab)
        ? ('10-Pay' as PaymentOption)
        : paymentOption;

    if (nextPaymentOption !== paymentOption) {
      handlePaymentOptionChange(nextPaymentOption);
    }
    fetchQuotesForPolicyType(tab, nextPaymentOption);
  }

  function handleOnClose() {
    localStorage.removeItem('benefitDurationNotice');
    localStorage.removeItem('inflationProtectionNotice');
    onClose();
  }

  const handleRemoveFilter = useCallback(
    (filter: FilterName) => {
      if (filter === 'inflationProtection') {
        setValue('inflationProtection', DEFAULT_FILTERS_STATE.inflationProtection);
        setValue('inflationProtectionRange', DEFAULT_FILTERS_STATE.inflationProtectionRange);
      } else if (filter === 'inflationType') {
        setValue('inflationTypeFilter', DEFAULT_FILTERS_STATE.inflationTypeFilter);
      } else if (filter === 'benefitDuration') {
        setValue('benefitDuration', DEFAULT_FILTERS_STATE.benefitDuration);
      }
    },
    [setValue],
  );

  const activeFundingSourceOperation = useSelector(selectActiveFundingSourceOperation);
  const hasSelectedPolicies =
    Object.keys(client?.multipleFundingSources?.ltcPolicy ?? {}).length > 0;
  const hasQuotesLoaded = (quotes?.length ?? 0) > 0;

  const hasAnyReturnedQuotes =
    hasQuotesLoaded ||
    traditionalLTCQuotes.length > 0 ||
    (mode === 'benefit-design' && allPaymentOptionQuotes.length > 0);
  const isReloading = activeFundingSourceOperation !== null;
  const isInitialLoading = isLoading && !hasAnyReturnedQuotes;
  const hasUnexpectedError = isUnexpectedError(error);
  const knownLimitationError = isKnownLimitationError(error) ? error : null;
  const showContentArea = !isInitialLoading && !hasUnexpectedError;

  const isShortTermCareView = paymentOption === SHORT_TERM_CARE_PAYMENT_OPTION;

  const tabContent = useMemo(
    () => (
      <ShopInsuranceTabContent
        filteredAdjustedQuotes={filteredAdjustedQuotes}
        filteredTraditionalLTCQuotesWithEligibility={filteredTraditionalLTCQuotesWithEligibility}
        expandedIndices={expandedIndices}
        selectedTab={selectedTab}
        clientPartner={clientPartner}
        premiumValue={effectivePremiumValue}
        paymentOption={paymentOption}
        advisorId={advisorId}
        isQuoteSelected={isQuoteSelected}
        handleCardToggle={handleCardToggle}
        onClose={handleOnClose}
        knownLimitationError={knownLimitationError}
        hasSameGenderPartner={hasSameGenderPartner}
        mode={mode}
        benefitDesignQuoteGroups={benefitDesignQuoteGroups}
        stateOfResidence={state.stateAbbreviation}
      />
    ),
    [
      filteredAdjustedQuotes,
      filteredTraditionalLTCQuotesWithEligibility,
      expandedIndices,
      selectedTab,
      clientPartner,
      effectivePremiumValue,
      paymentOption,
      advisorId,
      isQuoteSelected,
      handleCardToggle,
      handleOnClose,
      knownLimitationError,
      hasSameGenderPartner,
      mode,
      benefitDesignQuoteGroups,
      state.stateAbbreviation,
    ],
  );

  // Count of quotes visible on screen after filters, matching whichever list the
  // current mode/payment option actually renders. Life and traditional LTC quotes
  // are never shown together — traditional belongs to the Annually/Monthly options.
  const isTraditionalPayment = TRADITIONAL_LTC_PAYMENT_OPTIONS.includes(paymentOption);
  const visibleQuoteCount =
    mode === 'benefit-design'
      ? benefitDesignQuoteGroups.length
      : isTraditionalPayment
        ? filteredTraditionalLTCQuotesWithEligibility.length
        : filteredAdjustedQuotes.length;

  const shopInsuranceFiltersProps = {
    showEligibilityFilter: flags.nycLifeDemo ? false : showEligibilityFilter,
    eligibilityCounts,
    mode,
    handleModeChange,
    benefitDesignAvailableOptions,
    benefitDesignDraftFilters,
    availableCarriers,
    visibleQuoteCount,
  };

  const isMarriedOrPartnered = isClientMarriedOrPartnered(
    client?.intakeSurvey?.clientMaritalStatus,
    hasPartnerWithDemographics,
  );

  const activeFilterChips = useShopInsuranceActiveFilters({
    mode,
    paymentOption,
    premiumValue: effectivePremiumValue,
    premiumRange: activePremiumRange,
    appliedPremiumFilters: activeFilters,
    benefitDurationValue,
    inflationProtectionValue,
    inflationProtectionRangeValue,
    useMinimumInflation,
    inflationTypeFilterValue,
    client,
    quotes,
    isGapFillMode: isCoverageGapEnabled && isGapFillMode,
    showLifeFilters: !TRADITIONAL_LTC_PAYMENT_OPTIONS.includes(paymentOption),
    showEligibilityFilter: !flags.nycLifeDemo && showEligibilityFilter,
    onRemovePremiumFilter: handleRemoveFilter,
    onRemoveBenefitDesignFilter: benefitDesignDraftFilters.handleRemoveAppliedFilter,
  });

  const maxCoverageQuote = useMemo(() => {
    if (!adjustedQuotes.length) return undefined;
    return adjustedQuotes.reduce((best, q) =>
      (q.projectedCostCoverage ?? 0) > (best.projectedCostCoverage ?? 0) ? q : best,
    );
  }, [adjustedQuotes]);

  return (
    <div
      className="relative flex h-full flex-col overflow-hidden"
      data-testid="shop-insurance-content"
    >
      <ShopInsuranceContext.Provider
        value={{
          sliderBounds,
          paymentOption,
          handlePaymentOptionChange,
          showTraditionalLTCOptions,
          showShortTermCareOption: isShortTermCareEligible,
          showSinglePayAnd10Pay,
          show5Pay: hasFivePayQuotes ?? paymentOption === '5-Pay',
          show20Pay: is20PayEligibleOnTab(selectedTab),
          maxCoverageQuote:
            isCoverageGapEnabled && maxCoverageQuote
              ? {
                  premiumAmount: maxCoverageQuote.premiumAmount,
                  projectedCostCoverage: maxCoverageQuote.projectedCostCoverage ?? 0,
                  maxAchievableCoverage:
                    maxCoverageQuote.maxAchievableCoverage ??
                    maxCoverageQuote.projectedCostCoverage ??
                    0,
                  carrierName: maxCoverageQuote.carrierName,
                }
              : undefined,
          isGapFillMode: isCoverageGapEnabled && isGapFillMode,
          setGapFillMode: isCoverageGapEnabled ? setGapFillMode : () => {},
        }}
      >
        <FormProvider {...formMethods}>
          <ShopInsuranceAcknowledgmentOverlay
            open={showAcknowledgment && !hasAcknowledged}
            onAcknowledge={handleAcknowledgment}
          />

          {/* HEADER — sticky above scroll */}
          <div className="border-border bg-background sticky top-0 z-40 shrink-0 border-b px-4">
            <div className="mx-auto w-full max-w-7xl">
              <ShopInsuranceHeader
                isLoading={isLoading}
                error={hasAnyReturnedQuotes ? null : error}
                onClose={handleOnClose}
              />
            </div>
          </div>

          {/* BODY — scrollable area */}
          <div
            ref={scrollContainerRef}
            className="relative min-h-0 flex-1 overflow-y-auto px-4 pt-6 lg:pt-0"
          >
            {isReloading && (
              <div className="bg-background/50 fixed inset-0 z-10 grid h-screen w-screen place-items-center">
                <Loader className="text-primary size-8 animate-spin" />
              </div>
            )}
            <div className="mx-auto w-full max-w-7xl">
              {isInitialLoading && (
                <div
                  className="flex min-h-[60vh] items-center justify-center"
                  data-testid="shop-insurance-loading"
                >
                  <LoadingSpinner testId="shop-insurance-loading-spinner" />
                </div>
              )}
              {hasUnexpectedError && (
                <div className="py-10">
                  <QuoteErrorDisplay
                    error={error}
                    onRetry={() => fetchQuotesForPolicyType(selectedTab, paymentOption)}
                  />
                </div>
              )}

              {showContentArea && (
                <div className="flex flex-col lg:flex-row lg:gap-x-8">
                  {/* SIDEBAR — fixed width, own scroll if content overflows */}
                  <ShopInsuranceFiltersSidebar {...shopInsuranceFiltersProps} />

                  {/* RIGHT COLUMN */}
                  <div className="flex flex-1 flex-col">
                    {!isShortTermCareView && (
                      <div className="space-y-3 pt-6 empty:hidden">
                        <AiUnderwritingCtaAlert />
                      </div>
                    )}
                    {!isShortTermCareView && (
                      <div className="bg-background sticky top-0 z-50 -mx-1 shrink-0 space-y-2 px-1 pb-2 pt-6 empty:hidden">
                        {mode === 'premium-optimization' && isCoverageGapEnabled && (
                          <GapFillBanner />
                        )}
                        <AdjustedQuotesBanner filters={activeFilterChips} />
                      </div>
                    )}

                    {isShortTermCareView ? (
                      <div className="pt-6">
                        <ShortTermCareShopView
                          advisorId={advisorId}
                          stateAbbreviation={state.stateAbbreviation}
                          clientBirthDate={client.intakeSurvey.clientBirthDate}
                          tobacco={isTobacco(client.intakeSurvey)}
                        />
                      </div>
                    ) : (
                      <div className="pt-6">
                        <ShopInsuranceTabsWrapper
                          hasPartnerWithDemographics={hasPartnerWithDemographics}
                          isMarriedOrPartnered={isMarriedOrPartnered}
                          paymentOption={paymentOption}
                          selectedTab={selectedTab}
                          selectedBuyerOption={selectedBuyerOption}
                          tabContent={tabContent}
                          resultsHeaderAction={
                            activePremiumRange ? (
                              <div className="flex shrink-0 items-center gap-2">
                                <span className="text-muted-foreground text-sm">Sort by</span>
                                <Select
                                  value={premiumSort}
                                  onValueChange={value => {
                                    setExpandedIndices([]);
                                    formMethods.setValue('premiumSort', value as PremiumSort);
                                  }}
                                >
                                  <SelectTrigger
                                    className="h-10 w-56"
                                    data-testid="shop-insurance-results-premium-sort"
                                    aria-label="Sort premium range results"
                                  >
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="premium-low">Lowest Premium</SelectItem>
                                    <SelectItem value="projected-coverage">
                                      Highest Projected Coverage
                                    </SelectItem>
                                    <SelectItem value="modeled-roi">Highest ROI</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            ) : undefined
                          }
                          handleTabChangeWithFetch={handleTabChangeWithFetch}
                          handleBuyerOptionChange={handleBuyerOptionChange}
                          featureFlags={featureFlags}
                          mode={mode}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {showContentArea && (
              <>
                <div className="bg-muted mx-auto mt-16 w-full max-w-7xl rounded-xl border p-4">
                  <ShopInsuranceNotice />
                </div>
                <AdvisorCustomDisclosures className="mx-auto mb-40 mt-2 w-full max-w-7xl" />
              </>
            )}
          </div>
        </FormProvider>
      </ShopInsuranceContext.Provider>

      {hasSelectedPolicies && (
        <div className="pointer-events-none fixed bottom-0 left-0 right-0 z-20 flex justify-center md:bottom-6">
          <div className="bg-background pointer-events-auto flex w-full flex-wrap gap-2 rounded-none border-t px-3 py-4 shadow-xl shadow-black/5 md:w-max md:flex-nowrap md:rounded-full md:border md:py-3">
            <Button
              size="lg"
              variant="outline"
              className="h-10 rounded-full px-4 text-sm md:h-11 md:px-6 md:text-base"
              onClick={() =>
                navigate(`/clients/${client.clientId}/onboarding/policy-application-dashboard`)
              }
            >
              Start Application
            </Button>
            <Button
              size="lg"
              className="h-10 rounded-full px-4 text-sm md:h-11 md:px-6 md:text-base"
              onClick={() => setShowComparePolicies(true)}
            >
              Compare Policies
            </Button>
          </div>
        </div>
      )}

      <PoliciesComparisonModal
        open={showComparePolicies}
        onOpenChange={setShowComparePolicies}
      />

      {!isD2C && (
        <>
          <PartnerRequiredDialog
            open={showPartnerRequiredDialog}
            onOpenChange={setShowPartnerRequiredDialog}
            onTakeMeToIt={() => {
              setShowPartnerRequiredDialog(false);
              setShowAddPartnerDialog(true);
            }}
            onLinkExisting={
              isAdvisor
                ? () => {
                    setShowPartnerRequiredDialog(false);
                    setShowLinkExistingDialog(true);
                  }
                : undefined
            }
          />

          {isAdvisor && (
            <LinkExistingPartnerDialog
              open={showLinkExistingDialog}
              client={client}
              onClose={() => setShowLinkExistingDialog(false)}
            />
          )}

          <AddPartnerDialog
            open={showAddPartnerDialog}
            onOpenChange={setShowAddPartnerDialog}
            clientId={client.clientId}
            onSubmitSuccess={() => {
              setShowAddPartnerDialog(false);
              setSelectedTab('joint');
              setExpandedIndices([]);
            }}
          />
        </>
      )}
    </div>
  );
}
